import { lazy, Suspense, useEffect, useRef, useState } from 'react';

import useRoutes from '@/hooks/useRoutes';
import { planInitialState, setCurrentPlan } from '@/redux/planSlice';
import { FunnelType } from '@/types/types';
import getSelectedPlan from '@/utils/getSelectedPlan';
import { useAppDispatch, useAppSelector } from '../../../hooks/useRedux';
import { resetPrices, setFunnelType } from '../../../redux/bookingSlice';
import { resetDiscountStates } from '../../../redux/discountSlice';
import ChristmasExplanation from './christmasExplanation/ChristmasExplanation';
import Cover from './cover/Cover';
import styles from './Home.module.scss';
import HowItWorks from './howItWorks/HowItWorks';
import Newsletter from './newsletter/Newsletter';
import OurExperiences from './ourExperiences/OurExperiences';
import OurIdentity from './ourIdentity/OurIdentity';
import SurpriseTripGift from './surpriseTripGift/SurpriseTripGift';
import WhatIncluded from './whatIncluded/WhatIncluded';

const SoloKuber = lazy(() => import('../../globals/soloKubers/SoloKuber'));
const GroupPopup = lazy(() => import('../../globals/groupPopup/GroupPopup'));
const Opinions = lazy(() => import('./opinions/Opinions'));
const OurDestinations = lazy(() => import('./ourDestinations/OurDestinations'));
const MoreThan = lazy(() => import('./moreThan/MoreThan'));

const Home = () => {
    const plan = useAppSelector((state) => state.plan.currentPlan);
    const adultsNumber = useAppSelector((state) => state.travellers.adults.length);
    const childrenNumber = useAppSelector((state) => state.travellers.children.length);
    const babiesNumber = useAppSelector((state) => state.travellers.babies.length);
    const [soloKuber, setSoloKuber] = useState<boolean>(false);
    const [isGroup, setIsGroup] = useState<boolean>(false);
    const totalTravellers = adultsNumber + childrenNumber + babiesNumber;
    const dispatch = useAppDispatch();
    const observerRef = useRef<IntersectionObserver | null>(null);
    const sectionContainerRef = useRef<HTMLDivElement>(null);
    const [belowIsVisible, setBelowIsVisible] = useState(false);
    const { routes, currentPathname } = useRoutes();
    const { isAloyPlan } = getSelectedPlan(plan);
    const isFlykubeHomePage = currentPathname === routes.home;
    const isAloyHomePage = currentPathname === routes.aloyHome;
    const currentPlanId = useAppSelector((state) => state.plan.currentPlan);

    useEffect(() => {
        observerRef.current = new IntersectionObserver(([entry]) => {
            if (entry.isIntersecting) {
                setBelowIsVisible(true);
                observerRef.current!.disconnect();
            }
        });

        observerRef.current!.observe(sectionContainerRef.current as HTMLElement);

        return () => {
            observerRef.current!.disconnect();
        };
    }, []);

    useEffect(() => {
        if (totalTravellers === 1 && !isAloyPlan) {
            setSoloKuber(true);
        } else {
            setSoloKuber(false);
        }

        if (totalTravellers >= 10 && isAloyPlan) {
            setIsGroup(true);
        } else {
            setIsGroup(false);
        }
    }, [totalTravellers]);

    useEffect(() => {
        dispatch(resetPrices());
        dispatch(resetDiscountStates());
    }, []);

    useEffect(() => {
        if (isAloyHomePage) {
            const aloyDefaultPlan = 9;
            dispatch(setCurrentPlan(aloyDefaultPlan));
            dispatch(setFunnelType(FunnelType.explore));
        } else {
            dispatch(setCurrentPlan(planInitialState.currentPlan));
            dispatch(setFunnelType(FunnelType.surprise));
        }
    }, [currentPathname]);

    return (
        <main>
            <Cover isChristmas={currentPlanId === 10} />
            {isFlykubeHomePage && currentPlanId !== 10 && <OurExperiences />}
            {currentPlanId !== 10 && <WhatIncluded isAloyPlan={!isFlykubeHomePage} />}
            {!isFlykubeHomePage && currentPlanId !== 10 && (
                <section className={styles.sectionOurDestinations}>
                    <OurDestinations isAloyPlan />
                </section>
            )}
            <section
                ref={sectionContainerRef}
                className={currentPlanId === 10 ? styles.christmasOpinionsContainer : styles.opinionsContainer}
            >
                <div
                    className={
                        currentPlanId === 10 ? styles.christmasOpinionsWhiteBackground : styles.opinionsWhiteBackground
                    }
                />
                <Opinions showTitle showWidget={belowIsVisible} isChristmas={currentPlanId === 10} />
            </section>
            {currentPlanId !== 10 && <SurpriseTripGift />}
            {isFlykubeHomePage && currentPlanId === 10 && <ChristmasExplanation />}
            {isFlykubeHomePage && currentPlanId !== 10 && <HowItWorks />}
            <Suspense fallback={null}>
                {belowIsVisible && isFlykubeHomePage && (
                    <section className={styles.sectionOurDestinations}>
                        <div className={styles.ourDestinationsWhiteBackground} />
                        <OurDestinations />
                    </section>
                )}
            </Suspense>
            <OurIdentity isAloyPlan={!isFlykubeHomePage} />
            <Suspense fallback={null}>{belowIsVisible && <MoreThan />}</Suspense>
            <Newsletter />
            {soloKuber && <SoloKuber setSoloKuber={setSoloKuber} />}
            {isGroup && <GroupPopup setIsGroup={setIsGroup} />}
        </main>
    );
};

export default Home;
